<template>
  <div class="page-wrapper">
    <div class="content container-fluid">
      <!-- HEADER -->
      <div class="page-header d-flex justify-content-between align-items-center">
        <div>
          <h3 class="page-title">
            <i class="fa fa-clipboard text-primary mr-2"></i>
            Quản lý Phiếu Công & Phiếu Lương
          </h3>
          <div class="text-muted small">Mở công → Nhân viên xác nhận → Gửi phiếu lương</div>
        </div>

        <div class="d-flex align-items-center">
          <input v-model="month" type="month" class="form-control mr-2" style="width:170px" />
          <button class="btn btn-outline-secondary" @click="load" :disabled="loading">
            <i class="fa fa-refresh mr-1"></i> Tải
          </button>
        </div>
      </div>

      <!-- KPI / ACTION BAR -->
      <div class="card mb-3 shadow-sm">
        <div class="card-body d-flex flex-wrap gap-2 align-items-center">
          <button class="btn btn-primary" @click="publishCongAll" :disabled="loading">
            <i class="fa fa-send mr-1"></i> Gửi phiếu công toàn bộ
          </button>

          <button class="btn btn-success" @click="sendLuongAll" :disabled="loading || stats.confirmed <= 0">
            <i class="fa fa-money mr-1"></i> Gửi phiếu lương (đã xác nhận)
          </button>

          <div class="ml-auto text-muted small">
            Tổng NV: <b>{{ stats.total }}</b> |
            Đã mở công: <b class="text-primary">{{ stats.published }}</b> |
            Đã xác nhận: <b class="text-success">{{ stats.confirmed }}</b> |
            Đã gửi lương: <b class="text-info">{{ stats.luongSent }}</b>
          </div>
        </div>
      </div>

      <!-- TABLE -->
      <div class="card shadow-sm">
        <div v-if="loading" class="p-4 text-center text-muted">
          <i class="fa fa-spinner fa-spin mr-1"></i> Đang tải dữ liệu...
        </div>

        <div v-else class="table-responsive">
          <table class="table table-hover mb-0">
            <thead class="thead-light">
              <tr>
                <th style="width:60px">#</th>
                <th>Nhân viên</th>

                <!-- tóm gọn từ matrix -->
                <th class="text-right" style="width:110px">Tổng công</th>
                <th class="text-right" style="width:90px">Trễ (p)</th>
                <th class="text-right" style="width:90px">Quên</th>

                <!-- workflow -->
                <th class="text-center" style="width:130px">Phiếu công</th>
                <th class="text-center" style="width:140px">NV xác nhận</th>
                <th class="text-center" style="width:130px">Phiếu lương</th>

                <th class="text-right" style="width:160px">Hành động</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(r, idx) in rows" :key="rowKey(r, idx)">
                <td>{{ idx + 1 }}</td>

                <td>
                  <div class="d-flex align-items-center">
                    <img
                      v-if="r.avatarUrl"
                      :src="r.avatarUrl"
                      class="rounded-circle mr-2"
                      style="width:32px;height:32px;object-fit:cover"
                      alt=""
                    />
                    <div>
                      <div class="font-weight-bold">
                        {{ r.hoTen || ("NV #" + r.userId) }}
                        <span class="text-muted" v-if="r.userId">(#{{ r.userId }})</span>
                      </div>
                      <div class="text-muted small">
                        {{ r.phongBan || "-" }} • Tháng {{ r.month || month }}
                      </div>
                    </div>
                  </div>
                </td>

                <!-- matrix summary -->
                <td class="text-right font-weight-bold">{{ fmt(r.totalCong) }}</td>
                <td class="text-right">{{ fmt(r.monthMinutesVaoTre) }}</td>
                <td class="text-right">
                  <span v-if="Number(r.monthQuenChamCount || 0) > 0" class="badge badge-danger">
                    {{ fmt(r.monthQuenChamCount) }}
                  </span>
                  <span v-else class="text-muted">0</span>
                </td>

                <!-- workflow badges -->
                <td class="text-center">
                  <span class="badge" :class="r.congThangNay ? 'badge-primary' : 'badge-secondary'">
                    {{ r.congThangNay ? "Đã gửi" : "Chưa" }}
                  </span>
                </td>

                <td class="text-center">
                  <span class="badge" :class="r.userConfirmed ? 'badge-success' : 'badge-warning'">
                    {{ r.userConfirmed ? "Đã xác nhận" : "Chưa" }}
                  </span>
                </td>

                <td class="text-center">
                  <span class="badge" :class="r.luongDaGui ? 'badge-info' : 'badge-secondary'">
                    {{ r.luongDaGui ? "Đã gửi" : "Chưa" }}
                  </span>
                </td>

                <td class="text-right">
                  <button
                    class="btn btn-sm btn-success"
                    title="Gửi phiếu lương"
                    @click="sendLuongOne(r)"
                    :disabled="loading || !r.userConfirmed || r.luongDaGui"
                  >
                    <i class="fa fa-paper-plane mr-1"></i> Gửi lương
                  </button>
                </td>
              </tr>

              <tr v-if="!rows.length">
                <td colspan="9" class="text-center text-muted py-4">Không có dữ liệu</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import api from "@/api"; // dùng chung instance axios
import wfService from "@/services/phieuCongLuongWorkflowService";

export default {
  name: "AdminPhieuCongLuong",

  data() {
    const now = new Date();
    return {
      month: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`,
      rows: [],
      loading: false,

      // raw sources
      matrix: null,
      wfList: [],
    };
  },

  computed: {
    stats() {
      const rows = this.rows || [];
      return {
        total: rows.length,
        published: rows.filter(x => !!x.congThangNay).length,
        confirmed: rows.filter(x => !!x.userConfirmed).length,
        luongSent: rows.filter(x => !!x.luongDaGui).length,
      };
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    rowKey(r, idx) {
      return r?.id || `${r?.userId || "u"}_${r?.month || this.month}_${idx}`;
    },

    fmt(v) {
      if (v === null || v === undefined) return "0";
      const n = Number(v);
      if (Number.isNaN(n)) return String(v);
      return n.toLocaleString("vi-VN");
    },

    async load() {
  this.loading = true;
  try {
    const [matrixRes] = await Promise.all([
      api.get("/api/cham-cong/admin/matrix", { params: { month: this.month } }),
    ]);

    this.matrix = matrixRes?.data?.data || null;

    const matrixRows = this.matrix?.rows || [];
    const userIds = matrixRows.map(x => x.userId).filter(Boolean);

    // IMPORTANT: gọi wf list có kèm userIds để BE ensure record
    const wfRes = await wfService.adminList(this.month, userIds);
    const wfData = wfRes?.data;
    this.wfList = Array.isArray(wfData) ? wfData : (wfData?.data || []);

    this.rows = this.mergeMatrixAndWorkflow(matrixRows, this.wfList || []);
  } catch (e) {
    this.rows = [];
    Swal.fire("Lỗi", e?.response?.data?.message || e?.message || "Không tải được dữ liệu", "error");
  } finally {
    this.loading = false;
  }
},

    mergeMatrixAndWorkflow(matrixRows, wfList) {
      // index workflow theo userId
      const wfMap = new Map();
      for (const w of (wfList || [])) {
        if (w?.userId == null) continue;
        wfMap.set(Number(w.userId), w);
      }

      // merge: ưu tiên thông tin NV từ matrix (đầy đủ hơn)
      return (matrixRows || []).map((m) => {
        const uid = Number(m.userId);
        const w = wfMap.get(uid);

        return {
          // key & info
          userId: uid,
          month: w?.month || this.month,
          hoTen: m.hoTen || w?.hoTen,
          phongBan: m.phongBan || w?.phongBan,
          avatarUrl: m.avatarUrl || w?.avatarUrl,

          // summary from matrix
          totalCong: m.totalCong,
          monthMinutesVaoTre: m.monthMinutesVaoTre,
          monthQuenChamCount: m.monthQuenChamCount,

          // workflow flags (3 trạng thái)
          congThangNay: !!w?.congThangNay,
          userConfirmed: !!w?.userConfirmed,
          luongDaGui: !!w?.luongDaGui,
        };
      });
    },

    async publishCongAll() {
  const ok = await Swal.fire({
    title: "Gửi phiếu công?",
    text: "Tất cả nhân viên sẽ thấy phiếu công để xác nhận",
    icon: "question",
    showCancelButton: true,
    confirmButtonText: "Gửi",
    cancelButtonText: "Hủy",
  });
  if (!ok.isConfirmed) return;

  this.loading = true;
  try {
    const userIds = (this.matrix?.rows || []).map(x => x.userId).filter(Boolean);
    await wfService.publishCongAll(this.month, userIds);

    Swal.fire("Thành công", "Đã gửi phiếu công", "success");
    await this.load();
  } catch (e) {
    Swal.fire("Lỗi", e?.response?.data?.message || e?.message || "Không gửi được phiếu công", "error");
  } finally {
    this.loading = false;
  }
},

    async sendLuongAll() {
  const ok = await Swal.fire({
    title: "Gửi phiếu lương?",
    text: "Chỉ gửi cho nhân viên đã xác nhận công",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Gửi",
    cancelButtonText: "Hủy",
  });
  if (!ok.isConfirmed) return;

  this.loading = true;
  try {
    const userIds = (this.matrix?.rows || []).map(x => x.userId).filter(Boolean);
    await wfService.sendLuongAll(this.month, userIds);

    Swal.fire("Thành công", "Đã gửi phiếu lương", "success");
    await this.load();
  } catch (e) {
    Swal.fire("Lỗi", e?.response?.data?.message || e?.message || "Không gửi được phiếu lương", "error");
  } finally {
    this.loading = false;
  }
},

    async sendLuongOne(row) {
      if (!row?.userId) return;

      this.loading = true;
      try {
        await wfService.sendLuongOne(row.userId, row.month || this.month);
        Swal.fire("Đã gửi", "Phiếu lương đã được gửi", "success");
        await this.load();
      } catch (e) {
        Swal.fire("Lỗi", e?.response?.data?.message || e?.message || "Không gửi được phiếu lương", "error");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
